import { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useLocalization } from '../contexts/LocalizationContext';
import { fetchAvailablePointRedemptions } from '../api/auth';
import type { Coupon } from '../types';
import PointRedemptionDialog from '../components/PointRedemptionDialog';


// Pastel gradient backgrounds cycling per card
const CARD_BGS = [
  'linear-gradient(135deg,#E8F4EC 0%,#C8E6D0 100%)',
  'linear-gradient(135deg,#FFF8E7 0%,#F5DFA0 100%)',
  'linear-gradient(135deg,#E8F0F8 0%,#B8D0E8 100%)',
  'linear-gradient(135deg,#F0F0F8 0%,#D0D0E8 100%)',
  'linear-gradient(135deg,#F8F0E8 0%,#E8D0B0 100%)',
  'linear-gradient(135deg,#F8E8F0 0%,#E8C0D0 100%)',
];

export default function RedeemPage() {
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const { t } = useLocalization();

  const { data, isLoading } = useQuery({
    queryKey: ['coupons', 'redeemable'],
    queryFn: fetchAvailablePointRedemptions,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const coupons = data ?? [];

return (
    <Box sx={{ flex: 1, backgroundColor: '#F6F6F6', display: 'flex', flexDirection: 'column', pb: '30px' }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: '8px', pb: 0, backgroundColor: '#fff', borderBottom: '1px solid #F0F0F0', position: 'sticky', top: 0, zIndex: 10 }}>
        <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1A1A1A', py: 1.5 }}>{t('redeem.title')}</Typography>

      </Box>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: '200px' }}>
          <CircularProgress sx={{ color: '#7BA88A' }} />
        </Box>
      ) : coupons.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ fontSize: '36px', mb: 1.5 }}>🎁</Typography>
          <Typography sx={{ fontSize: '15px', color: '#9A9A9A' }}>{t('redeem.empty.title')}</Typography>
        </Box>
      ) : (
        /* Reward grid */
        <Box sx={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '12px', px: 2, pt: '14px',
        }} role="list">
          {coupons.map((coupon, i) => (
            <Box
              key={coupon.couponId}
              component="button"
              type="button"
              role="listitem"
              onClick={() => setSelectedCoupon(coupon)}
              sx={{
                display: 'block', textAlign: 'left', background: '#fff',
                border: 'none', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
                cursor: 'pointer', fontFamily: 'inherit', padding: 0,
                transition: 'transform 0.12s ease',
                '&:active': { transform: 'scale(0.97)' },
              }}
            >
              {/* Image area */}
              <Box sx={{
                position: 'relative', width: '100%', aspectRatio: '1',
                background: CARD_BGS[i % CARD_BGS.length],
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {/* Points badge */}
                <Box sx={{
                  position: 'absolute', top: '8px', left: '8px',
                  background: '#E85A22', color: '#fff',
                  fontSize: '11px', fontWeight: 700,
                  borderRadius: '6px', px: '6px', py: '3px', lineHeight: 1,
                }}>
                  {(coupon.pointsToRedeem ?? 0) === 0
                    ? t('redeem.freeToClaim')
                    : `${(coupon.pointsToRedeem ?? 0).toLocaleString()} ${t('redeem.pts')}`}
                </Box>

                {/* Heart */}
                <Box sx={{ position: 'absolute', top: '8px', right: '8px', color: '#C0C0C0', display: 'flex' }}>
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <path d="M12 20s-7-4.4-7-9.2A3.8 3.8 0 0 1 12 8a3.8 3.8 0 0 1 7 2.8C19 15.6 12 20 12 20z" stroke="currentColor" strokeWidth="1.6"/>
                  </svg>
                </Box>

                {/* Center icon — first letter of coupon name */}
                <Typography sx={{ fontSize: '32px', fontWeight: 700, color: '#555', lineHeight: 1.2, userSelect: 'none' }}>
                  🎁
                </Typography>
              </Box>

              {/* Body */}
              <Box sx={{ p: '10px 12px 12px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3, mb: '6px' }}>
                  {coupon.name}
                </Typography>
                {(coupon.pointsToRedeem ?? 0) === 0 ? (
                  <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#7BA88A' }}>{t('redeem.freeToClaim')}</Typography>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg viewBox="0 0 12 12" width="12" height="12" fill="#C4A574">
                      <path d="M6 1.2l1.3 2.7 3 .4-2.2 2.1.5 3L6 8.2 3.4 9.4l.5-3L1.7 4.3l3-.4L6 1.2z"/>
                    </svg>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#C4A574' }}>
                      {(coupon.pointsToRedeem ?? 0).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {selectedCoupon && (
        <PointRedemptionDialog
          coupon={selectedCoupon}
          onClose={() => setSelectedCoupon(null)}
        />
      )}
    </Box>
  );
}
